"use client";

import { useId } from "react";
import type { InputHTMLAttributes, ReactNode, Ref } from "react";

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  helperText?: ReactNode;
  /** True for an error border only; a string also renders as the helper text. */
  error?: boolean | string;
  valid?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** "md" (default) is the 40px form control; "sm" is the 32px compact
   * control for toolbars, modal search rows, and dense tool UI. */
  size?: "sm" | "md";
}

function ValidIcon() {
  return (
    <svg
      className="ds-textfield__valid-icon"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.5 7.5L5.5 10.5L11.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TextField({
  label,
  inputRef,
  helperText,
  error,
  valid,
  iconLeft,
  iconRight,
  size = "md",
  className,
  id,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? `${autoId}-input`;
  const helperId = `${autoId}-helper`;

  const hasError =
    error === true || (typeof error === "string" && error.length > 0);
  const helper =
    typeof error === "string" && error.length > 0 ? error : helperText;
  const describedBy =
    [ariaDescribedBy, helper != null ? helperId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  const classes = [
    "ds-textfield",
    size === "sm" && "ds-textfield--sm",
    hasError && "ds-textfield--error",
    valid && !hasError && "ds-textfield--valid",
    rest.disabled && "ds-textfield--disabled",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      {label != null && (
        <label className="ds-textfield__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className="ds-textfield__control">
        {iconLeft != null && (
          <span className="ds-textfield__icon" aria-hidden="true">
            {iconLeft}
          </span>
        )}
        <input
          ref={inputRef}
          id={inputId}
          className="ds-textfield__input"
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {iconRight}
        {valid && !hasError && <ValidIcon />}
      </div>
      {helper != null && (
        <p id={helperId} className="ds-textfield__helper">
          {helper}
        </p>
      )}
    </div>
  );
}
